import {
  Component,
  ContentChildren,
  QueryList,
  AfterContentInit,
  AfterContentChecked,
  AfterViewInit,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  HostBinding,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  signal,
  inject,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { TabComponent } from '../tab/tab.component'
import { SelectComponent } from '../select/select.component'

@Component({
  selector: 'hyperkit-tab-bar',
  standalone: true,
  imports: [CommonModule, FormsModule, TabComponent, SelectComponent],
  templateUrl: './tab-bar.component.html',
  styleUrls: ['./tab-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabBarComponent
  implements AfterContentInit, AfterContentChecked, AfterViewInit, OnDestroy
{
  private cdr = inject(ChangeDetectorRef)

  @Input() variant: 'primary' | 'secondary' | 'tertiary' = 'primary'
  @Input() mode: 'underline' | 'text' = 'underline'
  @Input() controlled = false
  @Input() overflow: 'compact' | 'none' = 'none'
  @Input() activeTabValue: string | null = null
  @Output() tabSelect = new EventEmitter<TabComponent>()
  @Output() tabValueChange = new EventEmitter<string>()

  public selectedTab: TabComponent | null = null
  readonly isCompact = signal(false)

  tabData: { text: string; value: string }[] = []
  tabSelectOptions: { id: string; text: string }[] = []

  @HostBinding('style.width') width = '100%'

  @ContentChildren(TabComponent) tabs: QueryList<TabComponent> =
    new QueryList<TabComponent>()

  @ViewChild('tabHeadersMeasure')
  private tabHeadersMeasureRef?: ElementRef<HTMLElement>

  private resizeObserver?: ResizeObserver

  constructor(private elementRef: ElementRef) {}

  ngAfterContentInit(): void {
    if (!this.controlled) {
      this.tabs.forEach((tab) => {
        tab.tabClick.subscribe(() => this.onTabClick(tab))
      })
      this.initializeSelectedTab()
    }

    if (this.overflow === 'compact') {
      this.updateTabData()
      this.tabs.changes.subscribe(() => {
        this.updateTabData()
        this.cdr.markForCheck()
      })
    }

    this.updateTabVariants()
    this.cdr.markForCheck()
  }

  ngAfterContentChecked(): void {
    this.updateTabVariants()
  }

  ngAfterViewInit(): void {
    if (this.overflow !== 'compact') return

    this.resizeObserver = new ResizeObserver(() => {
      const natural = this.tabHeadersMeasureRef?.nativeElement.clientWidth ?? 0
      const available = this.elementRef.nativeElement.clientWidth
      this.isCompact.set(natural + 5 > available)
      this.cdr.markForCheck()
    })
    this.resizeObserver.observe(this.elementRef.nativeElement)
    if (this.tabHeadersMeasureRef?.nativeElement) {
      this.resizeObserver.observe(this.tabHeadersMeasureRef.nativeElement)
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect()
  }

  private updateTabData(): void {
    this.tabData = this.tabs.map((t) => ({
      text: t.text,
      value: t.value || t.text,
    }))
    this.tabSelectOptions = this.tabData.map((t) => ({
      id: t.value,
      text: t.text,
    }))
  }

  private updateTabVariants(): void {
    this.tabs.forEach((tab) => {
      if (tab.variant !== this.variant) {
        tab.variant = this.variant
      }
      if (tab.mode !== this.mode) {
        tab.mode = this.mode
      }
    })
  }

  onTabClick(tab: TabComponent): void {
    if (this.controlled) return

    this.tabs.forEach((t) => t.deselect())
    tab.selected = true
    this.selectedTab = tab

    this.tabSelect.emit(tab)

    this.cdr.markForCheck()
  }

  initializeSelectedTab(): void {
    const selectedTabs = this.tabs.filter((tab) => tab.selected)
    if (selectedTabs.length > 0) {
      this.selectedTab = selectedTabs[0]
    } else {
      this.selectedTab = this.tabs.first ?? null
      if (this.selectedTab) {
        this.selectedTab.selected = true
      }
    }

    if (this.selectedTab) {
      this.tabSelect.emit(this.selectedTab)
    }

    this.cdr.markForCheck()
  }

  get selectedTabValue(): string {
    if (this.activeTabValue) return this.activeTabValue
    const selected = this.tabs.find((t) => t.selected)
    return selected?.value || selected?.text || ''
  }

  onCompactSelectChange(selectedValue: any): void {
    this.tabValueChange.emit(selectedValue)
    this.cdr.markForCheck()
  }
}
